<script>
const PRIORITY_COLORS = {
  low: 'text-green-600',
  medium: 'text-yellow-600',
  high: 'text-orange-600',
  urgent: 'text-red-600',
};

const EVENT_LABELS = {
  first_response_time: 'FRT',
  every_response_time: 'ERT',
  resolution_time: 'RT',
};

function formatCountdownShort(remainingSeconds) {
  const absSeconds = Math.abs(remainingSeconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);

  if (remainingSeconds < 0) {
    return hours > 0 ? `-${hours}h` : `-${minutes || 1}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return '<1m';
}

function formatCountdownFull(remainingSeconds) {
  const absSeconds = Math.abs(remainingSeconds);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const sign = remainingSeconds < 0 ? '-' : '';

  if (hours > 0) {
    return `${sign}${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${sign}${minutes}m`;
  }
  return `${sign}<1m`;
}

export default {
  props: {
    slaPolicies: {
      type: Array,
      required: true,
    },
    priority: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      now: Date.now(),
      timer: null,
      isHovered: false,
    };
  },
  computed: {
    shouldHide() {
      return (
        !this.priority || !this.slaPolicies.length || this.status !== 'open'
      );
    },
    priorityColorClass() {
      return PRIORITY_COLORS[this.priority] || 'text-slate-600';
    },
    // Pick which events to display: FRT or ERT (whichever is present), plus RT when needed
    visibleEvents() {
      const frt = this.slaPolicies.find(p => p.event === 'first_response_time');
      const ert = this.slaPolicies.find(p => p.event === 'every_response_time');
      const rt = this.slaPolicies.find(p => p.event === 'resolution_time');

      const responseEvent = frt || ert;
      const events = [];

      if (responseEvent) {
        const remaining = this.remainingSecondsFor(responseEvent);
        const isOverdue =
          responseEvent.event_result === 'breached' || remaining < 0;
        events.push({
          label: EVENT_LABELS[responseEvent.event],
          remaining,
          isOverdue,
        });
      }

      // Show RT when: response event is breached/overdue, or no response event deadline exists
      const responseOverdue =
        responseEvent &&
        (responseEvent.event_result === 'breached' ||
          this.remainingSecondsFor(responseEvent) < 0);

      const showRt =
        rt && (!responseEvent || !responseEvent.deadline || responseOverdue);

      if (showRt) {
        const remaining = this.remainingSecondsFor(rt);
        const isOverdue = rt.event_result === 'breached' || remaining < 0;
        events.push({
          label: EVENT_LABELS[rt.event],
          remaining,
          isOverdue,
        });
      }

      return events;
    },
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    remainingSecondsFor(policy) {
      if (!policy.deadline) return 0;
      const deadline = new Date(policy.deadline).getTime();
      return Math.floor((deadline - this.now) / 1000);
    },
    formattedTime(event) {
      return this.isHovered
        ? formatCountdownFull(event.remaining)
        : formatCountdownShort(event.remaining);
    },
    eventColorClass(isOverdue) {
      return isOverdue ? 'text-red-600' : this.priorityColorClass;
    },
  },
};
</script>

<template>
  <span
    v-if="!shouldHide"
    class="relative inline-flex items-center"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <span
      class="inline-flex items-center gap-1.5 text-xxs font-semibold whitespace-nowrap"
      :class="{
        'absolute right-0 z-50': isHovered,
      }"
    >
      <span
        v-for="event in visibleEvents"
        :key="event.label"
        :class="eventColorClass(event.isOverdue)"
      >
        {{ event.label }}: {{ formattedTime(event) }}
      </span>
    </span>
  </span>
</template>
